import * as GeographicLib from 'geographiclib';
import {PolygonGrid} from '../models/polygon-grid';

/**
 * A polygon's grid, worked out: the lines to draw inside the shape, and how
 * many cells fit whole or in part.
 *
 * Everything happens in a flat frame in metres around the grid's anchor,
 * turned so the grid's axes are the frame's axes. The frame is azimuthal
 * equidistant on the WGS84 ellipsoid (see toLocalMetres): a side is as long
 * in it as the app measures it, so a 10 m wall holds ten 1 m cells exactly.
 * A frame scaled by a sphere's metre was 0.2-0.7% off those lengths, and a
 * room drawn to size counted a row of cut cells along its far walls. In the
 * turned frame a grid line is just `u = constant`, which makes clipping it to
 * the shape a matter of sorting where it crosses the edges.
 *
 * Holes are rings like the outline: a line is inside where it has crossed an
 * odd number of edges, so it stops at a hole and starts again after it.
 *
 * No google.maps here, so it can be tested on its own.
 */

type LatLng = {lat: number, lng: number};
type UV = {u: number, v: number};

const D2R = Math.PI / 180;
const geodesic = GeographicLib.Geodesic.WGS84;

/**
 * Where `p` is, in metres east (x) and north (y) of `origin`, on a plane where
 * every distance and direction from the origin is exact. Across the size of
 * a site any other distance on it is off by far less than a micrometre per
 * metre, and the 180th meridian is no seam: the longitude difference is
 * taken the short way round.
 */
export function toLocalMetres(origin: LatLng, p: LatLng): {x: number, y: number} {
  const r = geodesic.Inverse(origin.lat, origin.lng, p.lat, p.lng);
  const azimuth = r.azi1 * D2R;
  return {x: r.s12 * Math.sin(azimuth), y: r.s12 * Math.cos(azimuth)};
}

/** The point `x` metres east and `y` north of `origin` (the inverse of toLocalMetres). */
export function fromLocalMetres(origin: LatLng, x: number, y: number): LatLng {
  const s = Math.hypot(x, y);
  if (!(s > 0)) {
    return {lat: origin.lat, lng: origin.lng};
  }
  const r = geodesic.Direct(origin.lat, origin.lng, Math.atan2(x, y) / D2R, s);
  return {lat: r.lat2, lng: r.lon2};
}

/**
 * The bearing that runs the first axis of a grid anchored at `anchor` from
 * `from` towards `to`, exactly -- unrounded, so the grid's lines lie on that
 * side -- or null when the two are the same point.
 */
export function bearingAlong(anchor: LatLng, from: LatLng, to: LatLng): number | null {
  const a = toLocalMetres(anchor, from);
  const b = toLocalMetres(anchor, to);
  return gridBearing(b.x - a.x, b.y - a.y);
}

/**
 * The bearing that points a grid's first axis `east` and `north` (metres in
 * the frame of toLocalMetres), or null when that is no direction.
 */
export function gridBearing(east: number, north: number): number | null {
  if (Math.hypot(east, north) < 1e-9) {
    return null;
  }
  // The direction counter-clockwise from east; the grid's first axis points
  // there when the grid is turned clockwise by its negative.
  const phi = Math.atan2(north, east) / D2R;
  const bearing = ((-phi % 360) + 360) % 360;
  // A hair under 360 is due east: 0, not "360" in the panel.
  return bearing > 360 - 1e-9 ? 0 : bearing;
}

/** More lines than this and the grid is a grey smear; it is not drawn. */
export const MAX_GRID_LINES = 1500;
/** More cells than this are not counted: the count would stall the page. */
export const MAX_GRID_CELLS = 60000;
/** More cell centres than this are not drawn as dots: the map would crawl. They are still counted. */
export const MAX_GRID_DOTS = 3000;

export interface GridLines {
  /** Two-point segments, inside the shape. */
  segments: LatLng[][];
  /** Too many lines to draw at this cell size. */
  tooDense: boolean;
}

export interface GridCounts {
  /** Cells wholly inside the shape. */
  full: number;
  /** Cells the outline or a hole cuts through. */
  partial: number;
  /** Cells whose centre is inside the shape: one per tree, post or plant. */
  centres: number;
}

export interface GridCentres {
  /** The centres inside the shape, as drawn. */
  points: LatLng[];
  /** More than can be drawn at this cell size (none are given then). */
  tooMany: boolean;
}

class Frame {
  private readonly anchor: LatLng;
  private readonly cos: number;
  private readonly sin: number;

  constructor(grid: PolygonGrid) {
    this.anchor = {lat: grid.refLat, lng: grid.refLng};
    const theta = grid.bearing * D2R;
    this.cos = Math.cos(theta);
    this.sin = Math.sin(theta);
  }

  /**
   * Into the grid frame. The grid's first axis is east turned clockwise by the
   * bearing, (cos t, -sin t) in east/north metres; the second is north turned
   * the same way, (sin t, cos t).
   */
  toGrid(p: LatLng): UV {
    const {x, y} = toLocalMetres(this.anchor, p);
    return {u: x * this.cos - y * this.sin, v: x * this.sin + y * this.cos};
  }

  fromGrid(u: number, v: number): LatLng {
    const x = u * this.cos + v * this.sin;
    const y = -u * this.sin + v * this.cos;
    return fromLocalMetres(this.anchor, x, y);
  }
}

function ringsIn(frame: Frame, rings: LatLng[][]): UV[][] {
  return (rings || [])
    .filter(ring => ring && ring.length >= 3)
    .map(ring => ring.map(p => frame.toGrid(p)));
}

function extent(rings: UV[][]) {
  let uMin = Infinity, uMax = -Infinity, vMin = Infinity, vMax = -Infinity;
  rings.forEach(ring => ring.forEach(p => {
    if (p.u < uMin) { uMin = p.u; }
    if (p.u > uMax) { uMax = p.u; }
    if (p.v < vMin) { vMin = p.v; }
    if (p.v > vMax) { vMax = p.v; }
  }));
  return {uMin, uMax, vMin, vMax};
}

/**
 * Where the line `axis = c` crosses the rings, sorted. Half-open on each edge
 * (a <= c < b) so a vertex sitting on the line counts once, and an edge lying
 * along the line counts not at all -- a grid aligned to a side draws nothing
 * on top of that side.
 */
function crossings(rings: UV[][], axis: 'u' | 'v', c: number): number[] {
  const other = axis === 'u' ? 'v' : 'u';
  const out: number[] = [];
  rings.forEach(ring => {
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      if ((a[axis] <= c && b[axis] > c) || (b[axis] <= c && a[axis] > c)) {
        out.push(a[other] + (c - a[axis]) * (b[other] - a[other]) / (b[axis] - a[axis]));
      }
    }
  });
  return out.sort((x, y) => x - y);
}

/** The grid's lines inside the shape. `rings` is the outline, then any holes. */
export function gridLines(rings: LatLng[][], grid: PolygonGrid, maxLines = MAX_GRID_LINES): GridLines {
  const frame = new Frame(grid);
  const rs = ringsIn(frame, rings);
  if (!rs.length) {
    return {segments: [], tooDense: false};
  }
  const {uMin, uMax, vMin, vMax} = extent(rs);
  const w = grid.cellWidth, h = grid.cellHeight;
  const i0 = Math.ceil(uMin / w), i1 = Math.floor(uMax / w);
  const j0 = Math.ceil(vMin / h), j1 = Math.floor(vMax / h);
  const count = Math.max(0, i1 - i0 + 1) + Math.max(0, j1 - j0 + 1);
  if (count > maxLines) {
    return {segments: [], tooDense: true};
  }
  const minLength = Math.min(w, h) * 1e-6;
  const segments: LatLng[][] = [];
  for (let i = i0; i <= i1; i++) {
    const u = i * w;
    const xs = crossings(rs, 'u', u);
    for (let k = 0; k + 1 < xs.length; k += 2) {
      if (xs[k + 1] - xs[k] > minLength) {
        segments.push([frame.fromGrid(u, xs[k]), frame.fromGrid(u, xs[k + 1])]);
      }
    }
  }
  for (let j = j0; j <= j1; j++) {
    const v = j * h;
    const xs = crossings(rs, 'v', v);
    for (let k = 0; k + 1 < xs.length; k += 2) {
      if (xs[k + 1] - xs[k] > minLength) {
        segments.push([frame.fromGrid(xs[k], v), frame.fromGrid(xs[k + 1], v)]);
      }
    }
  }
  return {segments, tooDense: false};
}

/**
 * How many cells fit wholly inside the shape, and how many it cuts. Null when
 * the grid is too fine to count here (more than `maxCells` in the shape's box).
 *
 * A cell is whole when its four corners are inside or on the outline (so a
 * grid aligned to a side is whole along it), its centre is inside, and no edge
 * of the outline or of a hole passes through its interior. It is cut when an
 * edge passes through it, or its centre or a corner is strictly inside. A cell
 * that only touches the outline from outside -- or sits in a hole, its corners
 * on the hole's edge -- is neither.
 */
export function countCells(rings: LatLng[][], grid: PolygonGrid, maxCells = MAX_GRID_CELLS): GridCounts | null {
  const frame = new Frame(grid);
  const rs = ringsIn(frame, rings);
  if (!rs.length) {
    return {full: 0, partial: 0, centres: 0};
  }
  const {uMin, uMax, vMin, vMax} = extent(rs);
  const w = grid.cellWidth, h = grid.cellHeight;
  const i0 = Math.floor(uMin / w), i1 = Math.ceil(uMax / w) - 1;
  const j0 = Math.floor(vMin / h), j1 = Math.ceil(vMax / h) - 1;
  const cols = i1 - i0 + 1, rows = j1 - j0 + 1;
  if (cols <= 0 || rows <= 0) {
    return {full: 0, partial: 0, centres: 0};
  }
  if (cols * rows > maxCells) {
    return null;
  }
  // What counts as on a line: a tenth of a millimetre per metre of cell.
  // Coordinates that went through degrees and back are not exact, and walls
  // set to a length and an azimuth lean a few hundredths of a millimetre
  // from a true rectangle (meridians converge). The app measures to the
  // millimetre; a corner that little past a side must not turn a row partial.
  const eps = Math.max(w, h) * 1e-4;

  const edges: [UV, UV][] = [];
  rs.forEach(ring => ring.forEach((a, i) => edges.push([a, ring[(i + 1) % ring.length]])));

  // Corners are shared by four cells: test each once.
  const cornerState: Where[][] = [];
  for (let j = j0; j <= j1 + 1; j++) {
    const row: Where[] = [];
    for (let i = i0; i <= i1 + 1; i++) {
      row.push(where(rs, i * w, j * h, eps));
    }
    cornerState.push(row);
  }

  let full = 0, partial = 0, centres = 0;
  for (let j = j0; j <= j1; j++) {
    const v0 = j * h, v1 = v0 + h;
    // Only the edges reaching this row can cut a cell in it.
    const band = edges.filter(([a, b]) => Math.max(a.v, b.v) > v0 + eps && Math.min(a.v, b.v) < v1 - eps);
    for (let i = i0; i <= i1; i++) {
      const u0 = i * w, u1 = u0 + w;
      const ci = i - i0, cj = j - j0;
      const corners = [cornerState[cj][ci], cornerState[cj][ci + 1], cornerState[cj + 1][ci], cornerState[cj + 1][ci + 1]];
      let cut = false;
      for (const [a, b] of band) {
        if (Math.max(a.u, b.u) <= u0 + eps || Math.min(a.u, b.u) >= u1 - eps) {
          continue;
        }
        if (crossesInterior(a, b, u0, v0, u1, v1, eps)) {
          cut = true;
          break;
        }
      }
      const centre = where(rs, u0 + w / 2, v0 + h / 2, eps);
      if (centre === Where.INSIDE) {
        centres++;
      }
      if (!cut && centre === Where.INSIDE && corners.every(c => c !== Where.OUTSIDE)) {
        full++;
      } else if (cut || centre === Where.INSIDE || corners.some(c => c === Where.INSIDE)) {
        partial++;
      }
    }
  }
  return {full, partial, centres};
}

/**
 * The centres of the cells whose centre is inside the shape (strictly: not in
 * a hole, not on an edge) -- the same cells countCells counts as `centres`.
 * None, and `tooMany`, when there are more than `maxDots` to draw.
 */
export function cellCentres(rings: LatLng[][], grid: PolygonGrid, maxDots = MAX_GRID_DOTS): GridCentres {
  const frame = new Frame(grid);
  const rs = ringsIn(frame, rings);
  if (!rs.length) {
    return {points: [], tooMany: false};
  }
  const {uMin, uMax, vMin, vMax} = extent(rs);
  const w = grid.cellWidth, h = grid.cellHeight;
  const i0 = Math.floor(uMin / w), i1 = Math.ceil(uMax / w) - 1;
  const j0 = Math.floor(vMin / h), j1 = Math.ceil(vMax / h) - 1;
  if (i1 < i0 || j1 < j0) {
    return {points: [], tooMany: false};
  }
  if ((i1 - i0 + 1) * (j1 - j0 + 1) > MAX_GRID_CELLS) {
    return {points: [], tooMany: true};
  }
  const eps = Math.max(w, h) * 1e-4;
  const points: LatLng[] = [];
  for (let j = j0; j <= j1; j++) {
    for (let i = i0; i <= i1; i++) {
      const u = i * w + w / 2, v = j * h + h / 2;
      if (where(rs, u, v, eps) === Where.INSIDE) {
        if (points.length >= maxDots) {
          return {points: [], tooMany: true};
        }
        points.push(frame.fromGrid(u, v));
      }
    }
  }
  return {points, tooMany: false};
}

const enum Where { OUTSIDE, ON, INSIDE }

/** Even-odd test, with the outline (and holes' outlines) told apart. */
function where(rings: UV[][], u: number, v: number, eps: number): Where {
  let inside = false;
  for (const ring of rings) {
    for (let i = 0, k = ring.length - 1; i < ring.length; k = i++) {
      const a = ring[i], b = ring[k];
      if (onSegment(a, b, u, v, eps)) {
        return Where.ON;
      }
      if ((a.v > v) !== (b.v > v)
        && u < (b.u - a.u) * (v - a.v) / (b.v - a.v) + a.u) {
        inside = !inside;
      }
    }
  }
  return inside ? Where.INSIDE : Where.OUTSIDE;
}

function onSegment(a: UV, b: UV, u: number, v: number, eps: number): boolean {
  const cross = (b.u - a.u) * (v - a.v) - (b.v - a.v) * (u - a.u);
  const len = Math.hypot(b.u - a.u, b.v - a.v);
  if (Math.abs(cross) > eps * Math.max(1, len)) {
    return false;
  }
  return u >= Math.min(a.u, b.u) - eps && u <= Math.max(a.u, b.u) + eps
    && v >= Math.min(a.v, b.v) - eps && v <= Math.max(a.v, b.v) + eps;
}

/**
 * Does the segment pass through the open rectangle? Clipped to the closed
 * rectangle (Liang-Barsky), then the clipped piece's midpoint must lie
 * strictly inside: an edge running along a cell's side does not cut it.
 */
function crossesInterior(a: UV, b: UV, u0: number, v0: number, u1: number, v1: number, eps: number): boolean {
  let t0 = 0, t1 = 1;
  const du = b.u - a.u, dv = b.v - a.v;
  const clip = (p: number, q: number): boolean => {
    if (Math.abs(p) < 1e-15) {
      return q >= 0;
    }
    const r = q / p;
    if (p < 0) {
      if (r > t1) { return false; }
      if (r > t0) { t0 = r; }
    } else {
      if (r < t0) { return false; }
      if (r < t1) { t1 = r; }
    }
    return true;
  };
  if (!clip(-du, a.u - u0) || !clip(du, u1 - a.u) || !clip(-dv, a.v - v0) || !clip(dv, v1 - a.v)) {
    return false;
  }
  if (t1 - t0 <= 1e-12) {
    // A single point: a vertex. Inside the cell's interior, it cuts it.
    const u = a.u + du * t0, v = a.v + dv * t0;
    return u > u0 + eps && u < u1 - eps && v > v0 + eps && v < v1 - eps;
  }
  const t = (t0 + t1) / 2;
  const mu = a.u + du * t, mv = a.v + dv * t;
  return mu > u0 + eps && mu < u1 - eps && mv > v0 + eps && mv < v1 - eps;
}

/**
 * A first grid for a shape: along its longest side, anchored at that side's
 * start, cells a round number of `unitMeters` (1 for metres, 0.3048 for feet)
 * about a tenth of the shape across -- a starting point to adjust, laid the
 * way a tiler or an installer would start: square to the longest wall.
 */
export function suggestGrid(ring: LatLng[], unitMeters = 1): PolygonGrid | null {
  const aligned = alignToLongestSide(ring, {cellWidth: 1, cellHeight: 1});
  if (!aligned) {
    return null;
  }
  const frame = new Frame(aligned);
  const {uMin, uMax, vMin, vMax} = extent([ring.map(p => frame.toGrid(p))]);
  const across = Math.max(uMax - uMin, vMax - vMin);
  const size = niceSize(across / 10 / unitMeters) * unitMeters;
  return {...aligned, cellWidth: size, cellHeight: size};
}

/** The same cells, turned and anchored to the shape's longest side. */
export function alignToLongestSide(ring: LatLng[], cells: {cellWidth: number, cellHeight: number},
                                   show = true): PolygonGrid | null {
  if (!ring || ring.length < 2) {
    return null;
  }
  let best = -1, from: LatLng = null, to: LatLng = null;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    const len = geodesic.Inverse(a.lat, a.lng, b.lat, b.lng).s12;
    if (len > best) {
      best = len;
      from = a;
      to = b;
    }
  }
  if (!from || best <= 0) {
    return null;
  }
  // Exact, not rounded for show (the panel rounds what it shows): turned by
  // a hundredth of a degree off, the side cut through a whole row of cells
  // a few centimetres from the anchor.
  const bearing = bearingAlong(from, from, to);
  if (bearing === null) {
    return null;
  }
  return {
    cellWidth: cells.cellWidth,
    cellHeight: cells.cellHeight,
    bearing,
    refLat: from.lat,
    refLng: from.lng,
    show,
  };
}

/** 1, 2, 2.5 or 5 times a power of ten, nearest to `x`. */
function niceSize(x: number): number {
  if (!(x > 0) || !isFinite(x)) {
    return 1;
  }
  const pow = Math.pow(10, Math.floor(Math.log10(x)));
  const steps = [1, 2, 2.5, 5, 10];
  let best = steps[0] * pow;
  for (const s of steps) {
    if (Math.abs(s * pow - x) < Math.abs(best - x)) {
      best = s * pow;
    }
  }
  return best;
}
